// Framework-agnostic in-memory request cache (TTL + bounded size).
// Imperative — consumers read/write it directly and do not re-render on
// changes, so this is a plain singleton rather than a reactive store.
// (Replaces the retired CacheContext; `useCache()` returns this singleton.)

const MAX_CACHE_SIZE = 500
const TTL = 5 * 60 * 1000

const store = new Map()

// Subscribers let a mounted list (keyed by its own cache key, e.g. an
// apiUrl containing "pending"/"parking") learn when a matching entry is
// invalidated so it can refetch live, instead of only picking up fresh
// data on its next mount.
const subscribers = new Set()

// Guards against a subscriber callback that (directly or indirectly) calls
// deleteKey/clear again on a matching key — without this, that would
// re-notify the same subscriber mid-callback and recurse infinitely.
const notifying = new Set()

const notify = (matches) => {
  subscribers.forEach((entry) => {
    if (notifying.has(entry) || !matches(entry.key)) return
    notifying.add(entry)
    try {
      entry.callback()
    } finally {
      notifying.delete(entry)
    }
  })
}

export const cache = {
  get(key) {
    const item = store.get(key)
    if (!item) return null
    if (Date.now() > item.expiry) {
      store.delete(key)
      return null
    }
    return item.value
  },
  set(key, value) {
    if (store.size >= MAX_CACHE_SIZE) {
      const firstKey = store.keys().next().value
      store.delete(firstKey)
    }
    store.set(key, { value, expiry: Date.now() + TTL })
  },
  has: (key) => store.has(key),
  clear: () => {
    store.clear()
    notify(() => true)
  },
  deleteKey: (keyword) => {
    for (const key of store.keys()) {
      if (key.includes(keyword)) {
        store.delete(key)
      }
    }
    notify((key) => key.includes(keyword))
  },
  subscribe: (key, callback) => {
    const entry = { key, callback }
    subscribers.add(entry)
    return () => subscribers.delete(entry)
  },
}

export const useCache = () => cache
